import { DvalaError, RuntimeError } from '../../../../errors'
import type { Any } from '../../../../interface'
import { assertVector } from '../../../../typeGuards/annotatedCollections'
import { assertNumber } from '../../../../typeGuards/number'
import type { BuiltinNormalExpression, BuiltinNormalExpressions } from '../../../../builtin/interface'
import { toFixedArity } from '../../../../utils/arity'
import { geometricMeanReductionFunction, harmonicMeanReductionFunction, meanReductionFunction } from './mean'
import { medianReductionFunction } from './median'
import { prodReductionFunction } from './prod'
import { sumReductionFunction } from './sum'
import { sampleVarianceReductionFunction, varianceReductionFunction } from './variance'
import { sampleStdevReductionFunction, stdevReductionFunction } from './standardDeviation'
import { iqrReductionFunction } from './iqr'
import { spanReductionFunction } from './span'
import { sampleSkewnessReductionFunction, skewnessReductionFunction } from './skewness'
import { eccessKurtosisReductionFunction, kurtosisReductionFunction, sampleExcessKurtosisReductionFunction, sampleKurtosisReductionFunction } from './kurtosis'
import { rmsReductionFunction } from './rms'
import { madReductionFunction } from './mad'
import { medadReductionFunction } from './medad'
import { giniCoefficientReductionFunction } from './giniCoefficient'
import { entropyReductionFunction } from './entropy'

type VectorReductionKey<T extends string> = `${T}`
type VectorMovingWindowKey<T extends string> = `moving${Capitalize<T>}`
type VectorCenteredMovingWindowKey<T extends string> = `centeredMoving${Capitalize<T>}`
type VectorRunningKey<T extends string> = `running${Capitalize<T>}`

export type VectorReductionKeys<T extends string> = VectorReductionKey<T> | VectorMovingWindowKey<T> | VectorCenteredMovingWindowKey<T> | VectorRunningKey<T>

type ReductionFunction = (vector: number[]) => number
// type MovingWindowFunction = (vector: number[], windowSize: number, sourceCodeInfo: SourceCodeInfo | undefined) => number[]

export type ReductionFunctionDefinition<T extends string> = Record<VectorReductionKey<T>, ReductionFunction> & {
  minLength?: number
  padding?: number
}

export const reductionFunctionNormalExpressions: BuiltinNormalExpressions = {}

addReductionFunctions(meanReductionFunction)
addReductionFunctions(geometricMeanReductionFunction)
addReductionFunctions(harmonicMeanReductionFunction)
addReductionFunctions(medianReductionFunction)
addReductionFunctions(sumReductionFunction)
addReductionFunctions(prodReductionFunction)
addReductionFunctions(varianceReductionFunction)
addReductionFunctions(sampleVarianceReductionFunction)
addReductionFunctions(stdevReductionFunction)
addReductionFunctions(sampleStdevReductionFunction)
addReductionFunctions(iqrReductionFunction)
addReductionFunctions(spanReductionFunction)
addReductionFunctions(skewnessReductionFunction)
addReductionFunctions(sampleSkewnessReductionFunction)
addReductionFunctions(eccessKurtosisReductionFunction)
addReductionFunctions(kurtosisReductionFunction)
addReductionFunctions(sampleExcessKurtosisReductionFunction)
addReductionFunctions(sampleKurtosisReductionFunction)
addReductionFunctions(rmsReductionFunction)
addReductionFunctions(madReductionFunction)
addReductionFunctions(medadReductionFunction)
addReductionFunctions(giniCoefficientReductionFunction)
addReductionFunctions(entropyReductionFunction)

// Remove base variants that duplicate core built-ins (sum, prod, mean, median).
// The moving/centered-moving/running variants are unique to the vector module.
delete reductionFunctionNormalExpressions.sum
delete reductionFunctionNormalExpressions.prod
delete reductionFunctionNormalExpressions.mean
delete reductionFunctionNormalExpressions.median

function addReductionFunctions<T extends string>(fns: ReductionFunctionDefinition<T>) {
  for (const [key, value] of Object.entries(fns)) {
    /* v8 ignore next 3 */
    if (reductionFunctionNormalExpressions[key]) {
      throw new Error(`Duplicate normal expression key found: ${key}`)
    }
    if (key !== 'minLength' && key !== 'padding' && typeof value === 'function') {
      const reductionFn = value as ReductionFunction
      const baseKey = key.replace(/^/, '')
      const capKey = baseKey.charAt(0).toUpperCase() + baseKey.slice(1)
      const movingKey = `moving${capKey}` as VectorMovingWindowKey<T>
      const centeredMovingKey = `centeredMoving${capKey}` as VectorCenteredMovingWindowKey<T>
      const runningKey = `running${capKey}` as VectorRunningKey<T>
      const minLength = fns.minLength ?? 1
      assertNumber(minLength, undefined, { integer: true, finite: true, gte: 0 })
      reductionFunctionNormalExpressions[key] = createReductionNormalExpression(reductionFn, minLength)
      reductionFunctionNormalExpressions[movingKey] = createMovingNormalExpression(reductionFn, minLength)
      reductionFunctionNormalExpressions[centeredMovingKey] = createCenteredMovingNormalExpression(reductionFn, minLength, fns.padding ?? null)
      reductionFunctionNormalExpressions[runningKey] = createRunningNormalExpression(reductionFn, minLength)
    }
  }
}

function createReductionNormalExpression(
  reductionFunction: ReductionFunction,
  minLength: number,
): BuiltinNormalExpression<number> {
  return {
    evaluate: ([vector_], sourceCodeInfo) => {
      const vector = assertVector(vector_, sourceCodeInfo)
      if (vector.length < minLength) {
        throw new RuntimeError(`Vector length must be at least ${minLength}`, sourceCodeInfo)
      }

      try {
        return reductionFunction(vector)
      } catch (error) {
        throw new DvalaError(error, sourceCodeInfo)
      }
    },
    arity: toFixedArity(1),
  }
}

function createMovingNormalExpression(
  reductionFunction: ReductionFunction,
  minLength: number,
): BuiltinNormalExpression<Any> {
  return {
    evaluate: ([vector_, windowSize], sourceCodeInfo) => {
      const vector = assertVector(vector_, sourceCodeInfo)
      assertNumber(windowSize, sourceCodeInfo, { integer: true, finite: true, gte: minLength, lte: vector.length })
      if (vector.length === 0) {
        return [] as unknown as Any
      }
      try {
        if (windowSize >= vector.length) {
          return [reductionFunction(vector)] as unknown as Any
        }
        const result = []
        for (let i = 0; i < vector.length - windowSize + 1; i += 1) {
          result.push(reductionFunction(vector.slice(i, i + windowSize)))
        }
        return result as unknown as Any
      } catch (error) {
        throw new DvalaError(error, sourceCodeInfo)
      }
    },
    arity: toFixedArity(2),
  }
}

function createCenteredMovingNormalExpression(
  reductionFunction: ReductionFunction,
  minLength: number,
  padding: number | null,
): BuiltinNormalExpression<Any> {
  return {
    evaluate: ([vector_, windowSize, leftPadding, rightPadding], sourceCodeInfo) => {
      const vector = assertVector(vector_, sourceCodeInfo)
      if (vector.length < minLength) {
        throw new RuntimeError(`Vector length must be at least ${minLength}`, sourceCodeInfo)
      }

      assertNumber(windowSize, sourceCodeInfo, { integer: true, finite: true, gte: minLength, lte: vector.length })
      leftPadding = leftPadding ?? padding
      if (leftPadding !== null) {
        assertNumber(leftPadding, sourceCodeInfo, { finite: true })
      }
      rightPadding = rightPadding ?? padding
      if (rightPadding !== null) {
        assertNumber(rightPadding, sourceCodeInfo, { finite: true })
      }

      if (vector.length === 0) {
        return [] as unknown as Any
      }

      const halfWindowSize = Math.floor(windowSize / 2)
      const paddedVector = [
        ...Array<number | null>(halfWindowSize).fill(leftPadding),
        ...vector,
        ...Array<number | null>(halfWindowSize).fill(rightPadding),
      ]

      const start = typeof leftPadding === 'number' ? 0 : halfWindowSize
      const end = vector.length - (typeof rightPadding === 'number' ? 0 : (windowSize - halfWindowSize - 1))

      const result: (number | null)[] = [
        ...Array<null>(start).fill(null),
      ]

      try {
        for (let i = start; i < end; i += 1) {
          result.push(reductionFunction(paddedVector.slice(i, i + windowSize) as number[]))
        }
      } catch (error) {
        throw new DvalaError(error, sourceCodeInfo)
      }

      result.push(...Array<null>(vector.length - end).fill(null))
      return result as unknown as Any
    },
    arity: { min: 2, max: 4 },
  }
}

function createRunningNormalExpression(
  reductionFunction: ReductionFunction,
  minLength: number,
): BuiltinNormalExpression<Any> {
  return {
    evaluate: ([vector_], sourceCodeInfo) => {
      const vector = assertVector(vector_, sourceCodeInfo)
      if (vector.length < minLength) {
        throw new RuntimeError(`Vector length must be at least ${minLength}`, sourceCodeInfo)
      }

      if (vector.length === 0) {
        return [] as unknown as Any
      }

      try {
        const nullsCount = Math.max(minLength - 1, 0)
        const result: (number | null)[] = Array<null>(nullsCount).fill(null)

        for (let i = nullsCount; i < vector.length; i += 1) {
          result.push(reductionFunction(vector.slice(0, i + 1)))
        }
        return result as unknown as Any
      } catch (error) {
        throw new DvalaError(error, sourceCodeInfo)
      }
    },
    arity: toFixedArity(1),
  }
}
